import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack, useTheme } from '@ltvco/refresh-lib/theme';
import { isZeroed, DateUtil } from '@ltvco/refresh-lib/utils';
import { DebugMenu, IpFraudReport, ReportLoading } from '@ltvco/refresh-lib/v1';
import {
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  ReportNavigationMenuProps,
  ReportActionsWithDateUpdated,
  ReportRating,
  FraudIpReportOverview,
  AdditionalIpDetailsSection,
  ReportNullState,
} from '@ltvco/refresh-lib/v2';

import { ReportRouteProps } from 'components/reports/types';
import { getFraudIpNavLinkDataV2 } from 'navLinkData/fraudNavLinkData';

export function IpFraudReportPage({ permalink }: ReportRouteProps) {
  // hooks
  const queryResult = useReport(permalink);
  const zeroed = isZeroed(queryResult);
  const theme = useTheme();

  // constants
  const urlParams = new URLSearchParams(window.location.search);
  const reportType = 'ip-fraud';

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }
  const report = new IpFraudReport(queryResult?.data);
  const ipAddress = urlParams.get('ip_address') || report.data?.ip;
  document.title = `${ipAddress} - BeenVerified`;
  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  if (!report) return <ReportLoading menuItems={2} />;
  if (report?.data?.isEmpty) {
    return <ReportNullState />;
  }
  const fraudIpNavLinkDataV2 = getFraudIpNavLinkDataV2();
  return (
    <>
      <Box>
        <Stack direction="row">
          <Box
            sx={{
              display: { xs: 'none', lg: 'block' },
            }}
          >
            <ReportNavigationMenu
              reportType="Fraud Scan"
              headerTitle={report.data?.ip || 'IP Address'}
              menuItems={
                fraudIpNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
              }
            />
          </Box>
          <Box marginTop={0} width={'100%'}>
            <Box
              sx={{
                height: 30,
                backgroundColor: theme.palette.background.default,
                position: 'sticky',
                marginBottom: '-10px',
                marginX: -1,
                top: 52,
                zIndex: 10,
                display: {
                  xs: 'none',
                  md: 'block',
                },
              }}
            />

            <ReportActionsWithDateUpdated
              reportType={reportType}
              reportTitle="Ip Fraud"
              dateUpdated={reportUpdateDate}
              reportUpgraded={false}
            />

            <Stack spacing={2}>
              <FraudIpReportOverview reportData={report.data} />
              <AdditionalIpDetailsSection
                isDynamicConnection={report.data?.isDynamicConnection}
                isTrustedNetwork={report.data?.security.isTrustedNetwork}
                isMobile={report.data?.isMobile}
                asn={report.data?.asn}
                isSharedConnection={report.data?.isSharedConnection}
                isSecurityScanner={report.data?.security.isSecurityScanner}
                host={report.data?.host}
                isCrawler={report.data?.isCrawler}
              />
              <ReportRating
                rating={report.data.meta?.rating || null}
                report_type={reportType}
              />
            </Stack>
          </Box>
        </Stack>
        <ReportNavigationMobileMenu
          reportType="Fraud Scan"
          headerTitle={report.data?.ip || 'IP Address'}
          menuItems={
            fraudIpNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
          }
        />
        <DebugMenu menuItems={report.data.rawData.debug_menu} />
      </Box>
    </>
  );
}
